import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { CONTROL } from "../../constants/testIds";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "../ui/sonner";

// DEFECT-D-09: billing terms editable after provisioning too, feeding the platform dashboard's
// MRR/revenue KPIs directly (`PATCH /control/gyms/{id}/billing`).
export function BillingEditPanel({ orgId, subscription, canEdit }) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState("");
  const [cycle, setCycle] = useState("monthly");
  const [nextDate, setNextDate] = useState("");

  useEffect(() => {
    setAmount(subscription?.billing_amount != null ? String(subscription.billing_amount / 100) : "");
    setCycle(subscription?.billing_cycle || "monthly");
    setNextDate(subscription?.next_billing_date || "");
  }, [subscription]);

  const mutation = useMutation({
    mutationFn: () => api.patch(`/control/gyms/${orgId}/billing`, {
      billing_amount: amount ? Math.round(Number(amount) * 100) : undefined,
      billing_cycle: cycle, next_billing_date: nextDate || undefined,
    }),
    onSuccess: () => {
      toast.success("Billing terms updated.");
      queryClient.invalidateQueries({ queryKey: ["control-gym-detail", orgId] });
      queryClient.invalidateQueries({ queryKey: ["platform-summary"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div className="solid-surface rounded-[14px] p-5 space-y-3">
      <h3 className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>Billing terms</h3>
      <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Drives this gym's contribution to the platform's MRR figure on the dashboard.</p>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label>Billing amount (₹)</Label>
          <Input type="number" min={0} data-testid={CONTROL.billingAmountInput} value={amount} onChange={(e) => setAmount(e.target.value)} disabled={!canEdit} />
        </div>
        <div className="space-y-1.5">
          <Label>Cycle</Label>
          <Select value={cycle} onValueChange={setCycle} disabled={!canEdit}>
            <SelectTrigger data-testid={CONTROL.billingCycleSelect}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="annual">Annual</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Next billing date</Label>
          <Input type="date" data-testid={CONTROL.billingNextDateInput} value={nextDate} onChange={(e) => setNextDate(e.target.value)} disabled={!canEdit} />
        </div>
      </div>
      <Button data-testid={CONTROL.billingSaveButton} disabled={!canEdit || mutation.isPending} onClick={() => mutation.mutate()} className="text-white" style={{ background: "var(--scarlet-accent)" }}>
        Save billing terms
      </Button>
    </div>
  );
}
